import { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';

import { useAccountCompanyId } from 'application/hooks';
import { ModalTypes } from 'application/providers/ModalProvider';
import {
  selectIsAcceptanceAdvancedPolling,
  selectIsAcceptanceAdvancedSigningLoading,
  startAcceptanceAdvancedSigning,
} from 'application/store';
import { INTEGRATION_PAGE_PATH } from 'application/consts';
import { RouterUtils } from 'application/providers/RouterProvider';
import { useAppDispatch, useAppSelector } from 'application/providers/StoreProvider';

import { AcceptanceAdvanceStatus, AuthCurrentUserDocument, AuthCurrentUserQuery } from 'domain/api/graphql/generated';
import { LicenseType } from 'domain/billing/types';
import useAcceptanceAdvanced from 'domain/billing/useAcceptanceAdvanced';
import { FetchPolicyKeys } from 'domain/api/graphql/types';

import { UseSberBusinessOnlineViewModel } from './types';

const SBBOL_MESSAGE = 'SBBOL_BINDING_SUCCESS';
const SBBOL_REDIRECT = 'SBBOL_REDIRECT';
const IS_NEED_SHOW_UNPAID_LICENSES_MODAL = 'IS_NEED_SHOW_UNPAID_LICENSES_MODAL';
const URI_FOR_RETURN = `${window.location.origin}/${RouterUtils.generatePath(INTEGRATION_PAGE_PATH, { type: 'sbbol' })}`;

interface Props {
  isModalOpen: boolean;
  licensesCount: number;
  hasTrialLicenses: boolean;
  hasInactiveLicenses: boolean;
  hasActiveLicenses: boolean;
  renewableLicenses: LicenseType[];
  nonRenewableLicenses: LicenseType[];
  unpaidLicenses: LicenseType[];
  isDesktop: boolean;
  isIos: boolean;
  showCreatedInfoNotification: () => void;
  showPendingInfoNotification: () => void;
  showCancelledErrorNotification: () => void;
  showActiveTrialNotification: () => void;
  showActiveNotification: (isViewMode?: boolean) => void;
  closeAllModals: () => void;
  hideInfoNotification: () => void;
  openModal: (type: ModalTypes, props: {}) => void;
}

const useSberBusinessOnlineViewModel = ({
  isModalOpen,
  licensesCount,
  hasTrialLicenses,
  hasInactiveLicenses,
  hasActiveLicenses,
  nonRenewableLicenses,
  unpaidLicenses,
  isDesktop,
  isIos,
  showCreatedInfoNotification,
  showPendingInfoNotification,
  showCancelledErrorNotification,
  showActiveTrialNotification,
  showActiveNotification,
  openModal,
  closeAllModals,
  hideInfoNotification,
}: Props): UseSberBusinessOnlineViewModel => {
  const isPolling = useAppSelector(selectIsAcceptanceAdvancedPolling());
  const startSigningAcceptanceAdvancedLoading = useAppSelector(selectIsAcceptanceAdvancedSigningLoading());

  const dispatch = useAppDispatch();

  const companyId = useAccountCompanyId();

  const { data: userData } = useQuery<AuthCurrentUserQuery>(AuthCurrentUserDocument, {
    fetchPolicy: FetchPolicyKeys.CACHE_AND_NETWORK,
  });

  const currentCompany = userData?.currentUserPureQuery?.mainCompany;
  const canSberIdLink = Boolean(currentCompany?.canSberIdLink);
  const sberIdWasLinked = Boolean(currentCompany?.sberIdWasLinked);

  const [isReturnedFromIntegrationPage, setIsReturnedFromIntegrationPage] = useState<boolean>(false);

  const { acceptanceAdvanced, loading: acceptanceAdvancedLoading } = useAcceptanceAdvanced(companyId);

  const status = acceptanceAdvanced?.status || null;
  const payerAccount = acceptanceAdvanced?.payerAccount;

  const onStartSigningAcceptanceAdvanced = async () => {
    localStorage.setItem(SBBOL_REDIRECT, 'true');

    dispatch(
      startAcceptanceAdvancedSigning({
        companyId,
        uriForReturn: URI_FOR_RETURN,
        fallbackFn: () => {
          openModal(ModalTypes.SBERBUSSINESS_OPEN, {});
        },
      })
    );

    if (!isIos) {
      showCreatedInfoNotification();
    }
  };

  const handleOpenLicensesModal = () => {
    if (!isModalOpen) {
      if (hasTrialLicenses && licensesCount !== 0) {
        openModal(ModalTypes.LICENSES, {
          openModal,
          isViewMode: unpaidLicenses.length === 0 && nonRenewableLicenses.length === 0,
          unpaidLicensesOnly: false,
        });

        return;
      }

      if (hasInactiveLicenses && licensesCount !== 0) {
        openModal(ModalTypes.LICENSES, {
          openModal,
          isViewMode: false,
          unpaidLicensesOnly: true,
        });

        return;
      }

      if (hasActiveLicenses && licensesCount !== 0) {
        const unpaidLicensesOnly = localStorage.getItem(IS_NEED_SHOW_UNPAID_LICENSES_MODAL) === 'true';

        openModal(ModalTypes.LICENSES, {
          openModal,
          isViewMode: unpaidLicenses.length === 0 && nonRenewableLicenses.length === 0,
          unpaidLicensesOnly,
        });
      }
    } else if ((unpaidLicenses.length === 0 && nonRenewableLicenses.length === 0) || hasTrialLicenses) {
      closeAllModals();
      openModal(ModalTypes.LICENSES, {
        openModal,
        isViewMode: true,
        unpaidLicensesOnly: false,
      });
    }
  };

  const stopProcess = () => {
    hideInfoNotification();
    handleOpenLicensesModal();
    localStorage.removeItem(SBBOL_REDIRECT);
    localStorage.removeItem(IS_NEED_SHOW_UNPAID_LICENSES_MODAL);

    if (isDesktop) {
      if (hasTrialLicenses) {
        showActiveTrialNotification();
      } else {
        showActiveNotification();
      }
    }
  };

  useEffect(() => {
    switch (status) {
      case AcceptanceAdvanceStatus.Pending: {
        if (!isIos) {
          showPendingInfoNotification();
        }

        break;
      }
      case AcceptanceAdvanceStatus.Canceled: {
        showCancelledErrorNotification();
        break;
      }
      case AcceptanceAdvanceStatus.Authorized: {
        if (isPolling) {
          stopProcess();
        }

        break;
      }
      case AcceptanceAdvanceStatus.Active: {
        if (isPolling) {
          stopProcess();
        }

        break;
      }
      case null: {
        break;
      }
      default: {
        break;
      }
    }
  }, [status, isPolling]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || event.data.type !== SBBOL_MESSAGE) {
        return;
      }

      setIsReturnedFromIntegrationPage(true);
    };

    window.addEventListener('message', handleMessage);

    return () => window.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    if (isReturnedFromIntegrationPage) {
      if (status === AcceptanceAdvanceStatus.Pending) {
        showPendingInfoNotification();
      }
    }
  }, [isReturnedFromIntegrationPage]);

  const sberAuthButtonVisible = canSberIdLink || sberIdWasLinked;

  return {
    acceptanceAdvancedStatus: status,
    payerAccount: payerAccount || '',
    startSigningAcceptanceAdvancedLoading,
    acceptanceAdvancedLoading,
    sberAuthButtonVisible,
    onStartSigningAcceptanceAdvanced,
  };
};

export default useSberBusinessOnlineViewModel;
